/**
 * Cycle de vie d'un challenge — règles PURES, lues par le serveur (handlers, envois, scoring)
 * et par l'écran. Aucune dépendance serveur.
 *
 * Statuts (`challenge_status`) : draft → active ⇄ paused → ended.
 *
 * Vrai statut « en pause » (décisions de Valérie, 25/09 et 30/09/2026) : jusqu'ici la pause
 * remettait le challenge en `draft`. Désormais :
 *   - pause : active → paused ; reprise : paused → active ; la date de fin ne bouge PAS
 *     (le manager peut la prolonger lui-même) ;
 *   - pendant la pause, les parties ne sont PAS comptées dans le challenge (classement figé) ;
 *   - pendant la pause, AUCUNE notification de challenge ne part ;
 *   - un challenge en pause ne se supprime pas (décision 4.3 : brouillon seulement).
 */
export const CHALLENGE_STATUSES = ["draft", "active", "paused", "ended"] as const;
export type ChallengeStatus = (typeof CHALLENGE_STATUSES)[number];

/** Encore pilotable (modifier, participants, équipes) : tout sauf terminé. */
export function isChallengeOpen(status?: string | null): boolean {
  const s = status ?? "draft";
  return s === "draft" || s === "active" || s === "paused";
}

/** Un challenge qui a démarré (en cours ou en pause) : seuls quelques champs restent modifiables. */
export function isChallengeStarted(status?: string | null): boolean {
  return status === "active" || status === "paused";
}

/** Statut après « Mettre en pause » — `null` si le geste n'a pas de sens dans cet état. */
export function statusAfterPause(status?: string | null): ChallengeStatus | null {
  return status === "active" ? "paused" : null;
}

/** Statut après « Reprendre » — `null` si le geste n'a pas de sens dans cet état. */
export function statusAfterResume(status?: string | null): ChallengeStatus | null {
  return status === "paused" ? "active" : null;
}

/** Statut après « Clôturer » : un challenge en cours OU en pause peut être clôturé. */
export function statusAfterEnd(status?: string | null): ChallengeStatus | null {
  return status === "active" || status === "paused" ? "ended" : null;
}

/** Ce que la pause écrit en base : le statut, et l'HEURE de la pause (règle de rattachement des parties). */
export function pausePatch(now: Date): { status: "paused"; pausedAt: Date; updatedAt: Date } {
  return { status: "paused", pausedAt: now, updatedAt: now };
}

/**
 * Ce que la reprise écrit en base. La date de fin n'y figure JAMAIS : elle reste celle d'avant
 * la pause (décision du 30/09/2026) — le manager la prolonge s'il le souhaite.
 */
export function resumePatch(now: Date): { status: "active"; pausedAt: null; updatedAt: Date } {
  return { status: "active", pausedAt: null, updatedAt: now };
}

/**
 * La date de fin du challenge est-elle PASSÉE ? Même comparaison que le reste du dépôt
 * (`new Date(endDate) < now`). Sans date de fin : jamais passée.
 * Décision de Valérie (30/09/2026) : un challenge en pause dont la date de fin est passée ne se
 * reprend pas — on le prolonge d'abord (modification autorisée pendant la pause), puis on le reprend.
 */
export function challengeEndDatePassed(endDate?: Date | string | null, now: Date = new Date()): boolean {
  if (!endDate) return false;
  const t = new Date(endDate).getTime();
  return Number.isFinite(t) && t < now.getTime();
}

/** Peut-on LANCER une partie rattachée à ce challenge ? Seulement s'il est en cours. */
export function canPlayInChallenge(status?: string | null): boolean {
  return status === "active";
}

/** Pendant la pause, aucune notification de challenge (email, push, in-app). */
export function challengeSendsNotifications(status?: string | null): boolean {
  return status !== "paused";
}

/**
 * Une partie compte-t-elle dans le challenge ? Décision de Valérie (30/09/2026, point 5) : le
 * rattachement se fait selon l'HEURE DE DÉBUT de la partie, comparée à l'heure de mise en pause.
 *   - challenge qui n'est pas en pause : la partie compte ;
 *   - en pause : une partie COMMENCÉE AVANT la pause compte, même terminée pendant ; une partie
 *     commencée pendant la pause ne compte pas (elle est détachée au scoring) ;
 *   - heure de pause inconnue (challenges mis en pause avant la colonne `paused_at`) : la partie
 *     compte — aucune partie ne peut démarrer dans un challenge en pause (lancement refusé).
 */
export function sessionCountsForChallenge(
  status?: string | null,
  pausedAt?: Date | string | null,
  sessionStartedAt?: Date | string | null,
): boolean {
  if (status !== "paused") return true;
  if (!pausedAt || !sessionStartedAt) return true;
  return new Date(sessionStartedAt).getTime() < new Date(pausedAt).getTime();
}

/**
 * Supprimer une équipe : toujours dans un brouillon ; une fois le challenge démarré (en cours
 * ou en pause), seulement si elle est vide — ses membres perdraient sinon leur rattachement.
 */
export function canDeleteTeam(status: string | null | undefined, memberCount: number): boolean {
  if (!isChallengeOpen(status)) return false;
  return (status ?? "draft") === "draft" || memberCount === 0;
}
